import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";

interface RichTextProps {
    children: string | null | undefined;
    className?: string;
}

/**
 * RichText renders a text block as readable body copy: paragraphs (a blank line between them),
 * bullet or numbered lists, links (opened in a new tab), and the same inline formatting as RichLabel (bold, italics, ++underline++).
 *
 * @example
 * // In SQL seed file (E'' strings keep the line breaks):
 * E'**What taking part involves**\n\n- completing online questionnaires\n- sharing your experiences over time'
 */
export function RichText({ children, className }: RichTextProps) {
    if (!children) {
        return null;
    }

    const processedText = children.replace(/\+\+([^+]+)\+\+/g, "<u>$1</u>");

    return (
        <div className={className}>
            <Markdown
                rehypePlugins={[rehypeRaw]}
                components={{
                    p: ({ children }) => <p className="[&:not(:first-child)]:mt-3">{children}</p>,
                    strong: ({ children }) => <strong className="font-bold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    u: ({ children }) => <u className="underline">{children}</u>,
                    ul: ({ children }) => <ul className="space-y-1 mt-2 pl-6 list-disc">{children}</ul>,
                    ol: ({ children }) => <ol className="space-y-1 mt-2 pl-6 list-decimal">{children}</ol>,
                    li: ({ children }) => <li className="pl-1">{children}</li>,
                    a: ({ children, href }) => (
                        <a
                            href={href}
                            target="_blank"
                            rel="noreferrer"
                            className="text-primary underline underline-offset-2"
                        >
                            {children}
                        </a>
                    ),
                }}
                allowedElements={["p", "strong", "em", "u", "ul", "ol", "li", "br", "a"]}
                unwrapDisallowed
            >
                {processedText}
            </Markdown>
        </div>
    );
}

export default RichText;
